/**
 * Tools that wire interactions and clocks into objects' BehaviorGraphs, as
 * native graph data with no runtime scripts. They run one at a time with
 * the other tools that write behaviors (`serial`).
 */

import type { SceneObject } from '../../core/SceneObject';
import type { Interaction } from '../authoring/interactionGraph';
import { wireClock, wireInteractions, type Clock } from '../authoring/interactionOps';
import { interactionActions, interactionFromInput } from '../../creator-tools/build/interaction-input';
import { defineTool, fail, keep, refusalText, resolveRef, type ToolDefinition } from './input';
import type { JsonSchema, SceneTool, SceneToolHost, ToolOutcome } from './types';

/**
 * A tool's interaction as the operation takes it, with its object, scope and
 * action targets resolved by `resolve`. Throws on a reference that names
 * nothing; the operation checks everything else.
 */
export function readInteraction(input: Record<string, unknown>, resolve: (ref: string) => SceneObject | null): Interaction {
    // `distance` is a build's field; the wiring tools' schemas don't declare it.
    const { distance: _distance, ...interaction } = interactionFromInput(input, resolve);
    return interaction;
}

/** Advisory: a select or hover needs something under the object to pick. */
export function pickabilityWarning(object: SceneObject): string | null {
    const hasSurface = (node: SceneObject): boolean =>
        ['Model', 'Renderer', 'BoxCollider', 'SphereCollider'].some(type => node.getBehavior(type) != null) || node.getChildren().some(hasSurface);
    return hasSurface(object) ? null
        : `⚠ Pickability: "${object.name}" has no geometry or collider in its subtree. Provide a pickable surface and verify real pointer activation.`;
}

/** Wire `inputs`, each an interaction as a tool takes it, as one step. */
async function wire(host: SceneToolHost, inputs: unknown): Promise<ToolOutcome> {
    try {
        if (!Array.isArray(inputs) || inputs.length < 1 || inputs.length > 64) throw new Error('interactions must contain 1–64 entries');
        const interactions: Interaction[] = [];
        let actionCount = 0;
        const actionNames = new Set<string>();
        for (const [index, raw] of (inputs as unknown[]).entries()) {
            try {
                if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('Expected an interaction object');
                const input = raw as Record<string, unknown>;
                const actions = interactionActions(input);
                actionCount += actions.length;
                for (const action of actions) actionNames.add(String(action.action));
                if (actionCount > 256) throw new Error('A batch supports at most 256 actions');
                interactions.push(readInteraction(input, ref => resolveRef(host, ref)));
            } catch (error) {
                throw new Error(`interaction ${index + 1}: ${error instanceof Error ? error.message : String(error)}`);
            }
        }
        const result = await wireInteractions(interactions, host.ports());
        if ('refused' in result) return fail(`Error wiring interactions: ${refusalText(host, result.refused)}`);
        keep(host, 'wire_interaction', result);
        const reports = result.wired.map(({ object, event, summaries, update }) => ({ object: object.getPath(), event, actions: summaries.length, ...update }));
        const warnings = new Set<string>();
        for (const { object, event } of result.wired) {
            const warning = ['select', 'hover', 'hoverExit'].includes(event) ? pickabilityWarning(object) : null;
            if (warning) warnings.add(warning);
        }
        const objects = new Set(result.wired.map(({ object }) => object)).size;
        return {
            text: `Wired ${inputs.length} interactions (${actionCount} actions) on ${objects} objects — actions: ${[...actionNames].join(', ')}.\n`
                + `Graph update: ${JSON.stringify(reports)}${warnings.size ? '\n' + [...warnings].join('\n') : ''}`,
        };
    } catch (error) {
        return fail(`Error wiring interactions: ${error instanceof Error ? error.message : String(error)}`);
    }
}

const ACTION_PROPERTIES: Record<string, JsonSchema> = {
    action: { type: 'string', enum: ['setGroupState', 'setState', 'setProperty', 'setActive', 'toggleActive', 'setActiveChild', 'playAnimation', 'playMedia', 'pauseMedia', 'pagesNext', 'pagesPrev', 'navigate'] },
    target: { type: 'string' },
    state: { type: 'string' },
    property: { type: 'string' },
    value: {},
    expression: { type: 'string' },
    duration: { type: 'number', minimum: 0 },
    easing: { type: 'string', enum: ['linear', 'easeIn', 'easeOut', 'easeInOut'] },
    active: { type: 'boolean' },
    index: { type: 'integer', minimum: 0 },
    clip: { type: 'string' },
    loop: { type: 'boolean' },
    wrap: { type: 'boolean' },
    animate: { type: 'boolean' },
    url: { type: 'string' },
};
const EVENT_PROPERTIES: Record<string, JsonSchema> = {
    object: { type: 'string' },
    event: { type: 'string', enum: ['select', 'hover', 'hoverExit', 'start', 'tick'] },
    scope: { type: 'string' },
    when: { type: 'string' },
    mode: { type: 'string', enum: ['append', 'replace'] },
};
const ACTIONS: JsonSchema = { type: 'array', minItems: 1, maxItems: 32, items: { type: 'object', properties: ACTION_PROPERTIES, required: ['action'] } };

const INTERACTIONS: JsonSchema = {
    type: 'array', minItems: 1, maxItems: 64, items: { type: 'object', properties: { ...EVENT_PROPERTIES, actions: ACTIONS }, required: ['object', 'actions'] },
};

/** One interaction (`object` with `action` or `actions`), or several as `interactions`. */
const WIRE_INTERACTION: ToolDefinition = {
    name: 'wire_interaction',
    verb: 'Wiring',
    edits: true,
    inputSchema: {
        type: 'object',
        properties: { ...EVENT_PROPERTIES, ...ACTION_PROPERTIES, actions: ACTIONS, interactions: INTERACTIONS },
        oneOf: [
            { required: ['object', 'action'], not: { required: ['actions'] } },
            { required: ['object', 'actions'], not: { required: ['action'] } },
            { required: ['interactions'], not: { required: ['object'] } },
        ],
    },
    serial: true,
    run: (input, host) => input.interactions === undefined ? wire(host, [input])
        : input.object !== undefined ? Promise.resolve(fail('Error wiring interactions: give one interaction (`object`) or `interactions`, not both.'))
        : wire(host, input.interactions),
};
const wireInteractionTool = defineTool(WIRE_INTERACTION);

const wireClockTool = defineTool({
    name: 'wire_clock',
    verb: 'Wiring a clock on',
    edits: true,
    inputSchema: {
        type: 'object',
        properties: {
            object: { type: 'string' },
            property: { type: 'string' },
            period: { type: 'number' },
            rate_scale: { type: 'number' },
            rate_properties: { type: 'array', items: { type: 'string' }, maxItems: 4 },
            running_property: { type: 'string' },
        },
        required: ['object', 'property', 'period'],
    },
    serial: true,
    run: async (input, host) => {
        try {
            if (typeof input.object !== 'string') throw new Error('object is required');
            const object = resolveRef(host, input.object);
            if (!object) throw new Error(`object "${input.object}" not found`);
            const rates = input.rate_properties ?? [];
            if (!Array.isArray(rates) || rates.length > 4 || rates.some(name => typeof name !== 'string')) throw new Error('rate_properties must contain up to four property names');
            // The fields are the input, unchecked; the operation checks them.
            const clock: Clock = {
                property: input.property as string,
                period: input.period as number,
                rateScale: (input.rate_scale ?? 1) as number,
                rateProperties: rates as string[],
                runningProperty: input.running_property as string | undefined,
            };
            const result = await wireClock(object, clock, host.ports());
            if ('refused' in result) return fail(`Error: ${refusalText(host, result.refused)}`);
            keep(host, 'wire_clock', result);
            const { property, period, rateScale = 1, runningProperty } = clock;
            return {
                text: `Wired native clock "${object.name}.${property}" with period ${period}; rate = ${rateScale}${(rates as string[]).map(name => ` × ${name}`).join('')} per second`
                    + `${runningProperty ? ` while ${runningProperty}` : ''}. Existing non-tick events retained.\nGraph update: ${JSON.stringify(result.update)}`,
            };
        } catch (error) {
            return fail(`Error: ${error instanceof Error ? error.message : String(error)}`);
        }
    },
});

export const WIRING_TOOLS: readonly SceneTool[] = [wireInteractionTool, wireClockTool];
